import {isEqual} from 'lodash';

import {createResourceTableUpdatedReducer} from '../../../helpers/models/resourceHelpers';
import {InboxModel} from '../../../models/api/inboxModel';
import {TeammateInboxesUpdate} from '../../../models/api/teammateInboxesUpdateModel';
import {AppAction} from '../../appActions';
import {deleteFromResourceTable, ResourceMap, ResourceTable} from '../../appTypes';
import {DataGroupsActionsEnum} from '../dataGroupsActions';
import {InboxesActionsEnum} from './inboxesActions';

/*
 * Reducer.
 */

const inboxesUpdatedReducer = createResourceTableUpdatedReducer('inboxes', (inbox) => inbox.id, mergeInbox);

export function inboxesReducer(
  state: ResourceTable<number, InboxModel>,
  action: AppAction,
): ResourceTable<number, InboxModel> {
  switch (action.type) {
    // Data Group.
    case DataGroupsActionsEnum.DATA_UPDATED:
      return inboxesUpdatedReducer(state, action);

    // A teammate's access to inboxes was updated.
    case InboxesActionsEnum.TEAMMATE_INBOXES_UPDATED:
      return {
        ...state,
        byId: updateTeammateInboxes(state.byId, action.payload),
      };

    // Single inbox deletion.
    case InboxesActionsEnum.INBOX_DELETED:
      return deleteFromResourceTable(state, action.payload);

    default:
      return state;
  }
}

function mergeInbox(existingInbox: InboxModel | undefined, updatedInbox: InboxModel): InboxModel {
  // Some companies (such as Better Mortgage) get a lot of frivolous inbox_updated pusher events.
  if (
    existingInbox &&
    existingInbox.updatedAt === updatedInbox.updatedAt &&
    isEqual(existingInbox, updatedInbox)
  ) {
    return existingInbox;
  }

  return updatedInbox;
}

function updateTeammateInboxes(
  inboxes: ResourceMap<InboxModel>,
  update: TeammateInboxesUpdate,
): ResourceMap<InboxModel> {
  const {teammateId, addedToInboxes, removedFromInboxes} = update;

  // Copy the resource map and create a helper to patch it.
  const newInboxes = {...inboxes};
  const mutateInboxes = (
    keys: typeof addedToInboxes | typeof removedFromInboxes,
    updateTeammateIds: (teammateIds: ReadonlyArray<number>) => ReadonlyArray<number>,
  ) => {
    keys.forEach(({id, updatedAt}) => {
      const inbox = newInboxes[id];

      // Check the inbox exists and hasn't been updated more recently.
      if (!inbox || inbox.updatedAt > updatedAt) {
        return;
      }

      // Don't patch updatedAt, since this is an incomplete update.
      newInboxes[id] = {
        ...inbox,
        teammateIds: updateTeammateIds(inbox.teammateIds),
      };
    });
  };

  // Add the teammate to the given inboxes.
  mutateInboxes(addedToInboxes, (teammateIds) =>
    teammateIds.includes(teammateId) ? teammateIds : [...teammateIds, teammateId],
  );

  // Remove the teammate from the given inboxes.
  mutateInboxes(removedFromInboxes, (teammateIds) =>
    teammateIds.filter((inboxTeammateId) => inboxTeammateId !== teammateId),
  );

  return newInboxes;
}
